import type { FC } from 'react';
import { useState } from 'react';
import cx from 'classnames';
import { Button } from '@THC/components/Button';
import { Input } from '@THC/components/Input';
import { MDText } from 'i18n-react';

import { triggerAiCompanionSendQueryButtonClickedEvent } from 'Services/analytics/events/aiCompanionEvents';

import LocalTexts from '../../AiCompanion.texts';

import { Placeholder } from './Placeholder';

import styles from './MessageField.module.scss';

const LocalT = new MDText(LocalTexts);

interface MessageFieldProps {
	hasItems: boolean;
	isBusy: boolean;
	isMultiConversation?: boolean;
	onSendMessage: (message: string) => void;
	thinking: boolean;
}

export const MessageField: FC<MessageFieldProps> = ({
	hasItems,
	isBusy,
	isMultiConversation,
	onSendMessage,
	thinking,
}) => {
	const [value, setValue] = useState('');
	const [hasFocus, setHasFocus] = useState(false);

	const trimmedValue = value.trim();
	const isDisabled = !trimmedValue || thinking || isBusy;

	const onSubmit = () => {
		if (!isDisabled) {
			triggerAiCompanionSendQueryButtonClickedEvent();
			onSendMessage(trimmedValue);
			setValue('');
		}
	};

	const onKeyUp = (event: unknown) => {
		if ((event as KeyboardEvent).key === 'Enter') {
			onSubmit();
		}
	};

	const onChange = (event: unknown) => {
		const el = (event as { target: HTMLInputElement }).target;
		setValue(el.value);
	};

	const onBlur = () => {
		setHasFocus(false);
	};

	const onFocus = () => {
		setHasFocus(true);
	};

	return (
		<div className={cx(styles.input, hasItems && styles.fixed, isMultiConversation && styles.multiConversation)}>
			<Placeholder isBusy={isBusy} isRandom={!hasItems} isVisible={!hasFocus && !value} />
			<Input
				data-query-input
				fullWidth
				disabled={isBusy}
				formProps={{
					input: {
						onBlur,
						onChange,
						onFocus,
						value,
					},
				}}
				id="user-query"
				label=""
				onKeyUp={onKeyUp}
				showLabel={false}
			/>
			<Button
				data-query-send
				disabled={isDisabled}
				icon="arrow"
				label={LocalT.translate('sendButtonText')}
				onClick={onSubmit}
				showLabel={false}
				size="small"
			/>
			{hasItems && !isMultiConversation && <div className={styles.cover} />}
			{hasItems && isMultiConversation && <div className={styles.multiConversationCover} />}
		</div>
	);
};
